import { Card, CardContent } from '@mui/material'
import { useMemo, useState } from 'react'
import { defaultTo, find, isEmpty, isNil, or, prop, propEq } from 'ramda'
import { useSelector } from 'react-redux'
import { staffMappedSubdomain, studentMappedSubdomain } from './mappedSubdomain'
import { useGetCardsQuery } from 'api/cardsApi'

import Heading from './Heading'
import SubdomainSelection from './SubdomainSelection'
import CardGrid from './CardGrid'
import RelatedTopics from './RelatedTopics'

const CheckIn = () => {
  const { is_secondary_user } = useSelector(prop('user'))

  const { domain } = useSelector(prop('school'))

  const [selected, setSelected] = useState(null)
  const [currentPage, setCurrentPage] = useState(0)

  const selectedSubdomains = useMemo(() => {
    if (is_secondary_user)
      return defaultTo(
        [],
        prop('subdomains', find(propEq(selected, 'name'), staffMappedSubdomain))
      )
    return defaultTo(
      [],
      prop('subdomains', find(propEq(selected, 'name'), studentMappedSubdomain))
    )
  }, [selected, is_secondary_user])

  const selectedSearchTerm = useMemo(() => {
    if (isNil(selected)) return null
    if (is_secondary_user) {
      const found = find(propEq(selected, 'name'), staffMappedSubdomain)
      return prop('searchTerm', found)
    }
    const found = find(propEq(selected, 'name'), studentMappedSubdomain)
    return prop('searchTerm', found)
  }, [selected, is_secondary_user])

  const params = useMemo(
    () => ({
      'filter[types]': [1, 2, 3, 4, 5, 6, 7, 9, 10, 11, 12, 13],
      org_domain: domain,
      perPage: 21,
      search: selectedSearchTerm,
      take: 21
    }),
    [domain, selectedSearchTerm]
  )

  const { data = { cards: [] } } = useGetCardsQuery(params, {
    refetchOnMountOrArgChange: true,
    skip: isEmpty(selected)
  })

  const handleNextPage = () => {
    const totalCards = data.data?.length || 0
    const nextPage = currentPage + 1
    const startIndex = nextPage * 7

    // If we don't have enough cards for the next page, loop back to the beginning
    if (startIndex >= totalCards) {
      setCurrentPage(0)
    } else {
      setCurrentPage(nextPage)
    }
  }

  // Calculate the cards to display based on current page
  const currentCards = useMemo(() => {
    if (!data.data || data.data.length === 0) return []

    const startIndex = currentPage * 7
    const endIndex = startIndex + 7
    const totalCards = data.data.length

    // If we don't have enough cards from the current position, loop back
    if (startIndex >= totalCards) {
      return data.data.slice(0, 7)
    }

    let cardsToShow = data.data.slice(startIndex, endIndex)

    // If we need more cards to reach 7, add from the beginning
    if (cardsToShow.length < 7 && totalCards > 7) {
      const remainingNeeded = 7 - cardsToShow.length
      const additionalCards = data.data.slice(0, remainingNeeded)
      cardsToShow = [...cardsToShow, ...additionalCards]
    }

    return cardsToShow
  }, [data.data, currentPage])

  // Reset current page when selection changes
  const handleSelectionChange = newSelected => {
    setSelected(newSelected)
    setCurrentPage(0)
  }

  return (
    <Card
      sx={{
        backgroundColor: 'surface.low',
        width: 1
      }}
    >
      <Heading />
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4
        }}
      >
        <SubdomainSelection
          handleNextPage={handleNextPage}
          selected={selected}
          setSelected={handleSelectionChange}
        />
        <CardGrid cards={currentCards} selected={selected} />
        <RelatedTopics selectedSubdomains={selectedSubdomains} />
      </CardContent>
    </Card>
  )
}

export default CheckIn
